import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { format, addMonths, subMonths, eachDayOfInterval, isBefore, isSameDay, startOfToday, startOfDay, subDays } from 'date-fns';
import { ncbService } from '../services/ncbService';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import CalendarGrid from '../components/Calendar/CalendarGrid';

const { FiChevronLeft, FiChevronRight, FiArrowRight, FiClock, FiUsers, FiCoffee, FiShield, FiMessageSquare, FiCalendar, FiAlertCircle, FiCheckCircle, FiX, FiInfo } = FiIcons;

// Safe fallback rate
const FALLBACK_RATE = 3000;

const Rates = () => {
  const navigate = useNavigate();
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [blockedDatesSet, setBlockedDatesSet] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Dynamic Rate State - Initialized with fallback
  const [activeRate, setActiveRate] = useState(FALLBACK_RATE);
  
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [bookingStep, setBookingStep] = useState('select');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    guests: 2,
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [emailStatus, setEmailStatus] = useState('pending'); // pending, sent, failed
  const [submittedBooking, setSubmittedBooking] = useState(null);

  useEffect(() => {
    fetchData();
    window.scrollTo(0, 0);
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      try {
        const rates = await ncbService.getRateSettings();
        const standardRate = rates.find(r => r.rate_key === 'standard_nightly_rate' && r.status === 'active');
        if (standardRate && standardRate.rate_value) {
          const numericRate = Number(standardRate.rate_value);
          if (!isNaN(numericRate) && numericRate > 0) {
            setActiveRate(numericRate);
          }
        }
      } catch (rateErr) {
        console.warn("Failed to fetch dynamic rate, using fallback 3000:", rateErr);
      }

      const bookings = await ncbService.getBookings();
      const allBlocked = new Set();
      if (Array.isArray(bookings)) {
        bookings.forEach(b => {
          if (b.booking_status === 'pending' || b.booking_status === 'confirmed') {
            // Hotel-style: The end_date (check-out) is not an occupied night.
            // We only block nights from start_date to end_date - 1.
            const start = new Date(b.start_date);
            const end = subDays(new Date(b.end_date), 1);
            
            // Only generate interval if start is not after end (e.g. 1-night stay)
            if (!isBefore(end, start) || isSameDay(start, end)) {
              const dates = eachDayOfInterval({ start, end });
              dates.forEach(d => allBlocked.add(format(d, 'yyyy-MM-dd')));
            }
          }
        });
      }
      setBlockedDatesSet(allBlocked);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDateClick = useCallback((day) => {
    const targetDay = startOfDay(day);
    const today = startOfToday();
    const dateStr = format(targetDay, 'yyyy-MM-dd');
    const isBlocked = blockedDatesSet.has(dateStr);

    if (isBefore(targetDay, today)) return;

    if (!startDate || (startDate && endDate)) {
      // Selecting new start date - cannot be a blocked night
      if (isBlocked) return;
      setStartDate(targetDay);
      setEndDate(null);
    } else {
      // Selecting end date
      if (isBefore(targetDay, startDate)) {
        // If clicked before current start, treat as new start if not blocked
        if (!isBlocked) {
          setStartDate(targetDay);
        }
      } else if (isSameDay(targetDay, startDate)) {
        setStartDate(null);
      } else {
        // Validating the range [startDate, targetDay - 1]
        // This allows targetDay (check-out) to be a blocked date (next guest's check-in)
        const nights = eachDayOfInterval({ 
          start: startDate, 
          end: subDays(targetDay, 1) 
        });
        const hasBlocked = nights.some(d => blockedDatesSet.has(format(d, 'yyyy-MM-dd')));
        
        if (hasBlocked) {
          // If the range overlaps blocked nights, reset start to clicked day if not blocked
          if (!isBlocked) {
            setStartDate(targetDay);
          }
        } else {
          setEndDate(targetDay);
        }
      }
    }
  }, [startDate, endDate, blockedDatesSet]);

  const totalAmount = useMemo(() => {
    if (!startDate || !endDate) return 0;
    const diff = Math.max(1, Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)));
    return diff * activeRate;
  }, [startDate, endDate, activeRate]);

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setEmailStatus('pending');
    
    try {
      const guestEmailTrimmed = formData.email.trim();
      const adminEmail = "rvenuespaces@gmail.com";

      const payload = {
        booking_title: `${formData.name}'s Stay`,
        guest_name: formData.name,
        guest_email: guestEmailTrimmed,
        guest_phone: formData.phone,
        start_date: format(startDate, 'yyyy-MM-dd'),
        end_date: format(endDate, 'yyyy-MM-dd'),
        total_guests: parseInt(formData.guests),
        notes: formData.message,
        total_amount: totalAmount,
        booking_status: 'pending'
      };

      // 1. Save to Database (CRITICAL)
      console.log("[Rates] Saving booking to database...");
      await ncbService.createBooking(payload);
      console.log("[Rates] Booking saved successfully.");

      // 2. Prepare Template
      const specialRequestSection = formData.message ? `
        <div style="background-color:#FFFFFF; border-left:4px solid #4C6048; padding:14px 16px; margin:26px 0; border:1px solid #E8E8E0;">
          <p style="margin:0 0 6px; color:#A8B0A4; font-size:12px; letter-spacing:1.4px; text-transform:uppercase; font-weight:bold;">Special Requests</p>
          <p style="margin:0; font-size:14px; line-height:1.6; color:#344232;">${formData.message}</p>
        </div>` : '';

      const premiumEmailHtml = `
<!DOCTYPE html>
<html>
<head><meta charset="UTF-8" /><meta name="viewport" content="width=device-width, initial-scale=1.0" /></head>
<body style="margin:0; padding:0; background-color:#F4F4EC; font-family:Arial, sans-serif; color:#344232;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background-color:#F4F4EC; padding:20px;">
    <tr><td align="center">
      <table width="100%" style="max-width:600px; background-color:#FFFFFF; border-radius:15px; overflow:hidden; border:1px solid #E8E8E0; box-shadow:0 4px 12px rgba(0,0,0,0.05);">
        <tr><td style="background-color:#4C6048; padding:30px; text-align:center;">
          <img src="https://pub-46f2aff2774547b5b8e650f23b468b16.r2.dev/branding/Rvenue%20Apartelle%20Logo%20Cropped.png" alt="Logo" style="max-width:180px;" />
          <h1 style="color:#FFFFFF; font-size:20px; margin:15px 0 0; text-transform:uppercase; letter-spacing:1px;">Booking Request</h1>
        </td></tr>
        <tr><td style="padding:30px; line-height:1.6;">
          <h2 style="margin:0 0 14px; font-size:20px; color:#4C6048;">Hello, ${formData.name}!</h2>
          <p>We have received your booking request for <strong>R.Venue Apartelle</strong>. Our team will review your details and contact you shortly.</p>
          <div style="background:#F9F9F5; padding:20px; border-radius:10px; margin:20px 0; border:1px solid #E8E8E0;">
            <p style="margin:0 0 10px; font-size:12px; color:#4C6048; font-weight:bold; text-transform:uppercase;">Booking Summary</p>
            <p style="margin:5px 0;"><strong>Check-in:</strong> ${payload.start_date}</p>
            <p style="margin:5px 0;"><strong>Check-out:</strong> ${payload.end_date}</p>
            <p style="margin:5px 0;"><strong>Guests:</strong> ${payload.total_guests}</p>
            <p style="margin:5px 0;"><strong>Total:</strong> ₱${totalAmount.toLocaleString()}</p>
          </div>
          ${specialRequestSection}
          <p style="font-size:14px; color:#666; margin-top:30px;">Warm regards,<br/><strong style="color:#4C6048;">R.Venue Spaces Team</strong></p>
        </td></tr>
        <tr><td style="background-color:#F9F9F5; padding:20px; text-align:center; font-size:12px; color:#999; border-top:1px solid #E8E8E0;">
          R.Venue Apartelle • Lucban, Quezon
        </td></tr>
      </table>
    </td></tr>
  </table>
</body>
</html>`;

      // 3. Send Notification to Admin
      let adminSuccess = false;
      try {
        console.log("[Rates] Sending admin notification...");
        const adminResp = await fetch('/api/email/send', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            to: [adminEmail],
            subject: `New Booking Request – ${formData.name}`,
            html: premiumEmailHtml, 
            reply_to: guestEmailTrimmed
          })
        });
        
        const adminResult = await adminResp.json().catch(() => ({}));
        if (adminResp.ok) {
          adminSuccess = true;
          console.log("[Rates] Admin email sent.");
        } else {
          console.warn("[Rates] Admin email rejected:", adminResp.status, adminResult);
        }
      } catch (e) { 
        console.warn("[Rates] Admin email fetch error:", e.message); 
      }

      // 4. Short Delay to prevent rate limiting
      await new Promise(resolve => setTimeout(resolve, 800));

      // 5. Send Confirmation to Guest
      let guestSuccess = false;
      try {
        console.log("[Rates] Sending guest confirmation...");
        const guestResp = await fetch('/api/email/send', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            to: [guestEmailTrimmed],
            subject: `Booking Request Received – R.Venue Apartelle`,
            html: premiumEmailHtml, 
            reply_to: adminEmail
            // Removed bcc as it may not be supported and could cause payload rejection
          })
        });
        
        const guestResult = await guestResp.json().catch(() => ({}));
        if (guestResp.ok) {
          guestSuccess = true;
          console.log("[Rates] Guest email sent.");
        } else {
          console.warn("[Rates] Guest email rejected:", guestResp.status, guestResult);
        }
      } catch (e) { 
        console.warn("[Rates] Guest email fetch error:", e.message); 
      }

      // 6. Update Status
      setEmailStatus(adminSuccess && guestSuccess ? 'sent' : 'failed');

      setSubmittedBooking({
        ...payload,
        check_in: payload.start_date,
        check_out: payload.end_date,
        guests: payload.total_guests,
        email: guestEmailTrimmed,
        phone: formData.phone,
        total_price: payload.total_amount,
        message: payload.notes
      });
      setShowSuccessModal(true);
    } catch (err) {
      console.error("[Rates] Submission error:", err.message);
      alert(`Submission failed: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const proceedToSummary = () => {
    navigate('/booking-confirmation', { state: { bookingData: submittedBooking } });
  };

  return (
    <div className="w-full bg-brand-cream">
      <section className="pt-40 pb-20 bg-brand-olive text-white text-center">
        <div className="container mx-auto px-6">
          <p className="font-script text-3xl text-brand-cream mb-2">Rates & Availability</p>
          <h1 className="font-serif text-4xl md:text-6xl uppercase tracking-[0.2em]">Your Stay</h1>
        </div>
      </section>

      <section className="py-20">
        <div className="container mx-auto px-6 max-w-6xl">
          {error && (
            <div className="mb-12 bg-white p-8 border-l-4 border-red-500 shadow-xl flex items-center gap-6">
              <SafeIcon icon={FiAlertCircle} className="text-red-500 text-3xl shrink-0" />
              <div>
                <h4 className="font-serif text-lg text-brand-olive uppercase tracking-widest font-bold">System Connection Issue</h4>
                <p className="text-red-700 text-xs font-black uppercase tracking-widest mt-1">{error}</p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-20">
            <div className="lg:col-span-1 bg-brand-olive p-10 text-white shadow-2xl border border-white/5">
              <p className="text-[10px] font-black uppercase tracking-widest text-brand-cream/60 mb-4">Standard Rate</p>
              <div className="flex items-baseline gap-2 mb-6">
                <span className="text-5xl font-sans font-black tracking-tight">₱{activeRate.toLocaleString()}</span>
                <span className="text-white/60 text-xs font-bold uppercase tracking-widest">/ night</span>
              </div>
            </div>
            <div className="lg:col-span-2 bg-white p-10 shadow-xl border border-brand-olive/5 grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <h3 className="font-serif text-lg uppercase tracking-widest text-brand-olive border-b border-brand-olive/5 pb-3">Stay Inclusions</h3>
                <ul className="space-y-3">
                  {[{ icon: FiCoffee, text: "Private Kitchenette" }, { icon: FiUsers, text: "2-4 Guests" }, { icon: FiShield, text: "Secure Access" }].map((item, i) => (
                    <li key={i} className="flex items-center gap-4 text-sm text-brand-olive/70 font-bold uppercase tracking-widest">
                      <SafeIcon icon={item.icon} className="text-brand-olive" /> {item.text}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="space-y-6">
                <h3 className="font-serif text-lg uppercase tracking-widest text-brand-olive border-b border-brand-olive/5 pb-3">Guest Policy</h3>
                <ul className="space-y-3">
                  {[{ icon: FiClock, text: "2PM In / 11:00 AM Out" }, { icon: FiMessageSquare, text: "No Front Desk" }].map((item, i) => (
                    <li key={i} className="flex items-center gap-4 text-sm text-brand-olive/70 font-bold uppercase tracking-widest">
                      <SafeIcon icon={item.icon} className="text-brand-olive" /> {item.text}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          <AnimatePresence mode="wait">
            {bookingStep === 'select' ? (
              <motion.div key="select" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-8">
                <div className="flex flex-col md:flex-row items-center justify-between gap-6 bg-white p-8 border border-brand-olive/5 shadow-lg">
                  <h2 className="font-serif text-2xl uppercase tracking-widest text-brand-olive flex items-center gap-3"><SafeIcon icon={FiCalendar} /> Select Dates</h2>
                  <div className="flex items-center gap-8 bg-brand-cream px-6 py-3 rounded-full border border-brand-olive/10">
                    <button onClick={() => setCurrentMonth(subMonths(currentMonth, 1))} className="p-2 hover:text-brand-olive text-brand-olive/40 transition-all"><SafeIcon icon={FiChevronLeft} className="text-2xl" /></button>
                    <span className="font-sans font-black uppercase tracking-widest text-xs min-w-[140px] text-center text-brand-olive">{format(currentMonth, 'MMMM yyyy')}</span>
                    <button onClick={() => setCurrentMonth(addMonths(currentMonth, 1))} className="p-2 hover:text-brand-olive text-brand-olive/40 transition-all"><SafeIcon icon={FiChevronRight} className="text-2xl" /></button>
                  </div>
                </div>
                <div className="bg-white p-4 md:p-10 shadow-2xl border border-brand-olive/5">
                  {loading ? (
                    <div className="h-[400px] flex items-center justify-center"><div className="w-12 h-12 border-4 border-brand-olive/20 border-t-brand-olive rounded-full animate-spin" /></div>
                  ) : (
                    <CalendarGrid currentMonth={currentMonth} startDate={startDate} endDate={endDate} blockedDatesSet={blockedDatesSet} onDateClick={handleDateClick} />
                  )}
                </div>
                {startDate && endDate && (
                  <motion.div initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} className="fixed bottom-0 left-0 right-0 z-[60] bg-brand-olive p-6 text-white shadow-2xl">
                    <div className="container mx-auto max-w-6xl flex flex-col md:flex-row items-center justify-between gap-6">
                      <div className="text-center md:text-left">
                        <p className="text-[10px] uppercase tracking-widest font-black text-brand-cream/60 mb-1">Stay Period</p>
                        <div className="flex items-center gap-4 font-sans font-black text-xl md:text-3xl tracking-tight">
                          <span>{format(startDate, 'MMM dd')}</span>
                          <SafeIcon icon={FiArrowRight} className="text-brand-cream/30" />
                          <span>{format(endDate, 'MMM dd, yyyy')}</span>
                        </div>
                      </div>
                      <div className="flex flex-col items-center md:items-end gap-2">
                        <button onClick={() => setBookingStep('details')} className="btn-cream px-10 py-5 text-[10px] font-black uppercase tracking-widest">Request to Book • ₱{totalAmount.toLocaleString()}</button>
                      </div>
                    </div>
                  </motion.div>
                )}
              </motion.div>
            ) : (
              <motion.div key="details" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="max-w-3xl mx-auto bg-white p-10 md:p-16 shadow-2xl border border-brand-olive/5">
                <button onClick={() => setBookingStep('select')} className="text-[10px] font-black uppercase tracking-widest text-brand-olive/40 hover:text-brand-olive mb-10 flex items-center gap-2"><SafeIcon icon={FiChevronLeft} /> Change Dates</button>
                <h2 className="font-serif text-3xl uppercase tracking-widest text-brand-olive mb-12">Details</h2>
                <form onSubmit={handleBookingSubmit} className="space-y-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="space-y-2">
                      <label className="text-[10px] font-black uppercase tracking-widest text-brand-olive/60">Full Name</label>
                      <input type="text" required className="w-full px-0 py-3 border-b-2 border-brand-olive/10 focus:border-brand-olive outline-none text-brand-olive font-bold text-lg bg-transparent" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-black uppercase tracking-widest text-brand-olive/60">Guests</label>
                      <select className="w-full px-0 py-3 border-b-2 border-brand-olive/10 focus:border-brand-olive outline-none text-brand-olive font-bold text-lg bg-transparent" value={formData.guests} onChange={(e) => setFormData({...formData, guests: e.target.value})}>
                        {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n} {n === 1 ? 'Guest' : 'Guests'}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="space-y-2">
                      <label className="text-[10px] font-black uppercase tracking-widest text-brand-olive/60">Email</label>
                      <input type="email" required className="w-full px-0 py-3 border-b-2 border-brand-olive/10 focus:border-brand-olive outline-none text-brand-olive font-bold text-lg bg-transparent" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-black uppercase tracking-widest text-brand-olive/60">Phone</label>
                      <input type="tel" required className="w-full px-0 py-3 border-b-2 border-brand-olive/10 focus:border-brand-olive outline-none text-brand-olive font-bold text-lg bg-transparent" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase tracking-widest text-brand-olive/60">Message</label>
                    <textarea rows="4" className="w-full px-4 py-4 bg-brand-cream/30 border border-brand-olive/10 focus:border-brand-olive focus:bg-white outline-none text-brand-olive font-medium resize-none" value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} placeholder="Special requests?" />
                  </div>
                  <div className="space-y-4">
                    <button type="submit" disabled={isSubmitting} className="btn-primary w-full py-6">
                      {isSubmitting ? 'Sending Request...' : `Request to Book • ₱${totalAmount.toLocaleString()}`}
                    </button>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-brand-olive/40 text-center">No payment required yet. We’ll confirm availability before finalizing your booking.</p>
                  </div>
                </form>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* Booking Success Modal */}
      <AnimatePresence>
        {showSuccessModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[100] flex items-center justify-center bg-brand-olive/40 backdrop-blur-sm p-6">
            <motion.div initial={{ scale: 0.9, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.9, opacity: 0, y: 20 }} className="bg-white max-w-md w-full p-8 md:p-12 shadow-2xl relative overflow-hidden text-center">
              <button onClick={proceedToSummary} className="absolute top-4 right-4 text-brand-olive/40 hover:text-brand-olive transition-colors"><SafeIcon icon={FiX} className="text-2xl" /></button>
              
              <div className="w-20 h-20 bg-brand-cream rounded-full flex items-center justify-center mx-auto mb-8">
                <SafeIcon 
                  icon={emailStatus === 'sent' ? FiCheckCircle : FiInfo} 
                  className={`text-4xl ${emailStatus === 'sent' ? 'text-brand-olive' : 'text-amber-600'}`} 
                />
              </div>

              <h3 className="font-serif text-2xl uppercase tracking-widest text-brand-olive mb-4">
                {emailStatus === 'sent' ? 'Booking Request Received' : 'Booking Saved'}
              </h3>
              
              <p className="text-brand-olive/70 text-sm leading-relaxed mb-10">
                {emailStatus === 'sent' 
                  ? 'Thank you! Your booking request has been received. Our team will contact you shortly to confirm availability and payment details.'
                  : 'Your booking has been saved in our system. However, we were unable to deliver an email confirmation at this moment. Our team will still process your request.'
                }
              </p>
              
              <button onClick={proceedToSummary} className="btn-primary w-full py-4 text-[10px] uppercase tracking-widest font-black">View Booking Summary</button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Rates;